import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, MapPin } from "lucide-react";
import { useLocationStore } from "@/stores/useLocationStore";
import type { Tables } from "@stockuj/types";

type LocationSwitcherProps = {
  locations: Tables<"locations">[];
  canViewAll: boolean;
};

export function LocationSwitcher({ locations, canViewAll }: LocationSwitcherProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { currentLocationId, setLocation } = useLocationStore();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  // Don't render switcher if only 1 location and can't view all
  if (locations.length <= 1 && !canViewAll) return null;

  const currentName =
    currentLocationId === "all"
      ? "Wszystkie lokalizacje"
      : (locations.find((l) => l.id === currentLocationId)?.name ?? locations[0]?.name ?? "—");

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-button border border-border px-3 py-1.5 text-sm font-medium text-navy transition-colors hover:bg-gray-faint"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <MapPin size={14} className="text-gray-muted" />
        <span className="max-w-[140px] truncate">{currentName}</span>
        <ChevronDown
          size={14}
          className={`text-gray-muted transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-[220px] rounded-card border border-border bg-white py-1 shadow-subtle-lg">
          {canViewAll && (
            <>
              <button
                type="button"
                onClick={() => {
                  setLocation("all");
                  setOpen(false);
                }}
                className={`flex w-full items-center px-3 py-2 text-sm transition-colors ${
                  currentLocationId === "all"
                    ? "bg-navy/5 font-semibold text-navy"
                    : "text-navy hover:bg-gray-faint"
                }`}
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Wszystkie lokalizacje
              </button>
              <div className="my-1 border-t border-border" />
            </>
          )}

          {locations.map((loc) => (
            <button
              key={loc.id}
              type="button"
              onClick={() => {
                setLocation(loc.id);
                setOpen(false);
              }}
              className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors ${
                currentLocationId === loc.id
                  ? "bg-navy/5 font-semibold text-navy"
                  : "text-navy hover:bg-gray-faint"
              }`}
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              <span className="truncate">{loc.name}</span>
              {loc.is_primary && (
                <span className="ml-auto shrink-0 rounded-pill bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent">
                  Główna
                </span>
              )}
            </button>
          ))}

          <div className="my-1 border-t border-border" />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate("/ustawienia/lokalizacje");
            }}
            className="flex w-full items-center px-3 py-2 text-sm text-gray-muted transition-colors hover:bg-gray-faint hover:text-navy"
            style={{ fontFamily: "Inter, sans-serif" }}
          >
            Zarządzaj lokalizacjami
          </button>
        </div>
      )}
    </div>
  );
}
